/**
 * Headline figures a board page states about itself.
 *
 * A board's own aggregate spend is deliberately not among them: what one
 * company paid for one position is the product, and adding those figures up
 * produced a board-level total that said nothing a reader wanted.
 *
 * What a reader does want from a board is the money on offer, so the salary
 * stat is the median of the listings shown. The card a board row renders from
 * carries the salary as the label it prints — `"$205k – $245k per year"` — so
 * the figure is read back out of that label rather than plumbed separately.
 * Only annual dollar ranges count: an hourly rate or another currency averaged
 * into a salary median would misstate it, and a board with none of those falls
 * back to a stat every board can always state truthfully.
 */

import { centsToDollars, formatCents, type RankEntry } from '@bidtohire/shared';

import { compactCompensationLabel } from './format';

/** `$205,000`, `$205k`, `$1.2m` — every amount shape a label can carry. */
const AMOUNT = /\$(\d[\d,]*(?:\.\d+)?)([km])?/gi;

const SUFFIX_MULTIPLIER: Readonly<Record<string, number>> = { k: 1_000, m: 1_000_000 };

function amountToCents(digits: string, suffix: string | undefined): number {
  const units = Number(digits.replace(/,/g, ''));
  const multiplier = suffix === undefined ? 1 : (SUFFIX_MULTIPLIER[suffix.toLowerCase()] ?? 1);
  return Math.round(units * multiplier) * 100;
}

/**
 * The midpoint of an annual dollar salary range, in cents.
 *
 * Undefined for anything that cannot be compared against another listing on
 * the same ruler: a rate quoted per hour, day, week or month; a currency other
 * than dollars, which carries no `$`; and an open-ended `"From …"` or
 * `"Up to …"` bound, which states one end of a range rather than a range.
 */
export function annualSalaryMidpointCents(label: string | undefined): number | undefined {
  if (label === undefined || !label.endsWith(' per year')) return undefined;
  if (/^(from|up to)\b/i.test(label)) return undefined;

  const amounts = [...label.matchAll(AMOUNT)]
    .slice(0, 2)
    .map((match) => amountToCents(match[1] as string, match[2]));
  if (amounts.length === 0) return undefined;

  const low = amounts[0] as number;
  const high = amounts[1] ?? low;
  return Math.round((low + high) / 2 / 100) * 100;
}

/** The middle value, averaging the two middles when the count is even. */
export function medianCents(values: readonly number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[middle] as number;
  return Math.round(((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2 / 100) * 100;
}

/**
 * What the salary stat on a board says: the median where the listings shown
 * state comparable salaries, and the number of companies hiring where they do
 * not. Both are facts about the same rows, so neither can contradict the board
 * printed beneath it.
 */
export type BoardSalaryStat =
  | { readonly kind: 'median'; readonly cents: number }
  | { readonly kind: 'companies'; readonly count: number };

type SalaryFacts = Pick<RankEntry, 'compensationLabel' | 'company'>;

export function boardSalaryStat(entries: readonly SalaryFacts[]): BoardSalaryStat {
  const midpoints = entries
    .map((entry) => annualSalaryMidpointCents(entry.compensationLabel))
    .filter((cents): cents is number => cents !== undefined);

  const median = medianCents(midpoints);
  if (median !== undefined) return { kind: 'median', cents: median };

  const companies = new Set(entries.map((entry) => entry.company.slug));
  return { kind: 'companies', count: companies.size };
}

/** `$185k` — a salary stated at the width a stat tile has for it. */
export function compactMoneyLabel(cents: number): string {
  return compactCompensationLabel(formatCents(cents)) ?? `$${centsToDollars(cents)}`;
}
